import { getHistoryUrlParams } from '@/store/gamestate/gamestate'
import { SNK } from '@/helpers/helpers'
import { namespaces } from '@/helpers/constants'
import * as R from 'remeda'

let reportPollCurrentSessionId = null
let reportPollTimeoutResolve = null

const actions = {
  api_poker_solution({ commit, dispatch, getters, rootGetters }, { namespace } = {}) {
    const params = rootGetters[SNK('solparams_strategy', namespace)]

    const callId = `poker/solution/${new URLSearchParams(params)}`
    dispatch('api_call', {
      endpoint: 'poker/solution/',
      mutation: 'api_poker_solution',
      action: 'sol_data_handleApiCall',
      actionArgs: { params },
      params,
      hasCancel: false,
      dissIfSameCallInProgress: true,
      cacheCancelIdPart: 'poker/solution',
      callId,

      gwCacheId: callId,
      gwCacheAdd: true,
    })
  },
  api_poker_turn_preload({ commit, dispatch, rootGetters }, { namespace } = {}) {
    let history_spots = rootGetters[`${namespace ? `${namespace}/` : ''}history_spots`]
    const history_spot = rootGetters[`${namespace ? `${namespace}/` : ''}history_spot`]
    const street = rootGetters[`${namespace ? `${namespace}/` : ''}street`]
    const gameformat = rootGetters[`${namespace ? `${namespace}/` : ''}gameformat`]

    history_spots = history_spots.filter(e => e.id <= history_spot?.id)
    const hp = getHistoryUrlParams({ history_spots })

    const board = rootGetters[`${namespace ? `${namespace}/` : ''}street_board_any`](street)
      .filter(c => !c.includes('E'))
      .join('')
    const params = {
      gametype: gameformat.gametype,
      depth: gameformat.depth,
      stacks: gameformat.stacks,
      preflop_actions: hp.preflop_actions,
      flop_actions: hp.flop_actions,
      turn_actions: hp.turn_actions,
      river_actions: hp.river_actions,
      board,
    }

    dispatch('api_call', {
      endpoint: 'poker/turn/',
      params,
    })
  },
  api_poker_reports({ commit, dispatch, rootGetters }, { namespace } = {}) {
    const { endpoint, params } = rootGetters[SNK('solparams_reports', namespace)]
    const callId = `${endpoint}${new URLSearchParams(params)}`
    dispatch('api_call', {
      method: 'POST',
      endpoint,
      apiVersion: 'v4',
      mutation: 'api_poker_reports',
      action: 'rep_data_initialize',
      params, // this is weird but that endpoint dont run without it
      data: { ...params },

      hasCancel: false,
      dissIfSameCallInProgress: true,
      cacheCancelIdPart: endpoint,
      callId,
    })
  },
  async api_poker_custom_report_check({ commit, dispatch, rootGetters }) {
    dispatch('api_poker_custom_reports_cancel')

    const { params } = rootGetters[SNK('solparams_reports', namespaces.REPORTS)]
    const response = await dispatch(
      'api_call',
      {
        method: 'GET',
        endpoint: `aggregated-report/${params.custom_tree_id}/`,
        callId: 'custom-agg-report',
        params: params.preflop_actions
          ? {
              preflop_actions: params.preflop_actions, //
            }
          : {},
        apiVersion: 'v4',
        mutation: 'api_custom_aggregated_report_check',
      },
      { root: true },
    )
    dispatch('api_poker_custom_report_set_meta', response)

    // statuses:
    // done - show report
    // pending - show loading
    // not_exist - show buy btn
    // failed - show error
    const status = rootGetters.rep_data_customReportMeta.status

    const isInvalidStatus = !status || ['failed', 'not_exist'].includes(status)
    const isValidStatus = ['pending', 'done'].includes(status)

    if (isInvalidStatus) {
      const customReportMeta = { ...rootGetters.rep_data_customReportMeta }
      commit('reports_reset')
      commit('reports_set', {
        key: 'customReportMeta',
        value: { ...customReportMeta, status },
      })
    } else if (isValidStatus) {
      dispatch('api_poker_custom_report_get')
    }
  },
  api_poker_custom_report_set_meta({ commit }, response) {
    if (response?.status === 200 || response?.status === 206) {
      commit('reports_set', { key: 'customReportMeta', value: response.response?.data })
    } else {
      commit('reports_set', {
        key: 'customReportMeta',
        value: {
          status: 'failed',
          price: -1,
        },
      })
    }
  },
  async api_poker_custom_report_get({ commit, dispatch, rootGetters }) {
    commit('reports_set', {
      key: 'customReportMeta',
      value: { ...rootGetters.rep_data_customReportMeta, status: 'pending' },
    })

    const { params } = rootGetters[SNK('solparams_reports', namespaces.REPORTS)]
    const options = {
      method: 'POST',
      endpoint: 'aggregated-report/',
      callId: 'custom-agg-report',
      apiVersion: 'v4',
      action: 'rep_data_initialize',
      mutation: 'api_custom_aggregated_report',
      data: R.omit(params, ['offset', 'limit']),
      message: { type: 'error' },
      params,
      hasCancel: true,
      continueAfterCancel: true,
      canceledLoadingValue: false,
    }

    reportPollCurrentSessionId = Date.now()
    const response = await dispatch('api_poker_custom_reports_recursive_get', {
      options,
      reportPollSessionId: reportPollCurrentSessionId,
    })

    dispatch('api_poker_custom_report_set_meta', response)

    if (response.error) {
      console.warn('API call failed with error:', response.error)
    }
  },
  async api_poker_custom_reports_recursive_get(
    { commit, dispatch, rootGetters },
    {
      options,
      pollInterval = 2000, // 2 seconds between polls
      maxRetries = 10000, // Maximum attempts before giving up
      attempt = 0, // Current attempt count
      reportPollSessionId,
    },
  ) {
    if (reportPollCurrentSessionId !== reportPollSessionId) {
      return { response: { status: 'canceled_by_switch_to_diff_UI_context' } }
    }

    let result = await dispatch('api_call', options)

    // Update user info with credit balance (updates credits balance amount in the app bar)
    // Only refresh user info on the first custom reports poll call
    if (attempt === 0) {
      dispatch('api_call', {
        method: 'get',
        endpoint: 'account/settings/',
        hasCancel: false,
        mutation: 'api_account_settings',
        action: 'updateUserInfo',
      })
    }

    const data = result?.response?.data

    // This should happen if you use id of different user from the one logged in
    if (
      data?.custom_tree_id &&
      rootGetters[SNK('query_custree_id', namespaces.REPORTS)] !==
        result?.response?.data?.custom_tree_id
    ) {
      dispatch(SNK('router_update_query', namespaces.REPORTS), {
        query: {
          custree_id: data.custom_tree_id,
          solution_type: 'custom',
        },
        push: false,
        replace: true,
        saveLocally: true,
      })
      // hSpots watcher should retrigger and update gamestate with this new id and we reset it all and start again
      return result
    }

    if (data) {
      commit('reports_set', {
        key: 'customReportMeta',
        value: {
          ...rootGetters.rep_data_customReportMeta,
          solved_board_count: data.solved_board_count,
          total_board_count: data.total_board_count,
        },
      })
    }

    if (result?.response?.status === 206 || data?.status === 'pending') {
      // If max retries limit is reached, return the partial data and an error
      if (attempt >= maxRetries) {
        return {
          ...result,
          response: {
            status: 'max_retries_reached',
            data,
          },
          error: new Error('Max retries reached. Partial data returned.'),
        }
      }

      await new Promise(resolve => {
        reportPollTimeoutResolve = resolve
        setTimeout(resolve, pollInterval)
      })

      // Could be canceled in the middle if the timeout
      if (reportPollCurrentSessionId !== reportPollSessionId) {
        return { response: { status: 'canceled_by_switch_to_diff_UI_context' } }
      }

      // Recursive call to continue polling
      return dispatch('api_poker_custom_reports_recursive_get', {
        options,
        pollInterval,
        maxRetries,
        reportPollSessionId,
        attempt: ++attempt,
      })
    }

    return result
  },
  api_poker_custom_reports_cancel({ commit, dispatch, rootGetters }) {
    if (reportPollTimeoutResolve) {
      reportPollTimeoutResolve()
      reportPollTimeoutResolve = null
    }
    reportPollCurrentSessionId = null
    reportPollCurrentSessionId = Date.now()
    dispatch('api_cancel_pending_requests', { callIds: ['custom-agg-report'] })

    const status = rootGetters.rep_data_customReportMeta.status
    if (!['done', 'pending'].includes(status)) {
      commit('reports_reset')
    }
  },
}

const getters = {}

export { actions, getters }
